import { RESET_STATE as RESET_AUTH_STATE } from '@/modules/auth/store/mutationTypes';

export default class AuthService {
  /** @ngInject **/
  constructor(
    $http,
    $q,
    $state,
    $injector,
    $log,
    ngAuthSettings,
    userSettingsService,
    cacheService,
    promiseCacheService,
    VuexStore,
    VuexMutationTypes
  ) {
    this.$http = $http;
    this.$q = $q;
    this.$state = $state;
    this.$injector = $injector;
    this.$log = $log;
    this.ngAuthSettings = ngAuthSettings;
    this.userSettingsService = userSettingsService;
    this.cacheService = cacheService;
    this.promiseCacheService = promiseCacheService;
    this.VuexStore = VuexStore;
    this.VuexMutationTypes = VuexMutationTypes;

    this.serviceBase = ngAuthSettings.apiServiceBaseUri;
    this._authDataCacheKey = 'authService.authorizationData';

  }

  authentication = {
    isAuth: false,
    isSuper: false,
    userName: '',
    useRefreshTokens: false
  };

  saveRegistration(registration) {
    this.logOut();

    return this.$http.post(this.serviceBase + 'account/register', registration);
  }

  #stringToBool(input) {
    return /^true$/i.test(input);
  }

  // Never clear the device-trust token on an MFA challenge: the backend answers `two_auth_required`
  // for momentary reasons too, so discarding would turn a suspension into permanent loss of trust.
  /** Resolves to the response status and the MFA status its body states. */
  login(loginData) {
    const deviceTrustToken = this.getDeviceTrustToken(loginData.userName);

    let data = 'grant_type=password'
      + '&username=' + encodeURIComponent(loginData.userName)
      + '&password=' + encodeURIComponent(loginData.password)
      + '&client_id=' + this.ngAuthSettings.clientId;
    let loginResult;

    if (loginData.code) {
      data = data + '&code=' + loginData.code;
    }

    if (loginData.rememberDevice) {
      data = data + '&remember_device=true';
    }

    if (deviceTrustToken) {
      data = data + '&device_trust_token=' + encodeURIComponent(deviceTrustToken);
    }

    return this.$http.post(this.serviceBase + 'token', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
      .then((response) => {
        const mfaStatus = {
          enforceMfa: this.#stringToBool(response.data.enforceTwoAuth),
          hasMfa: this.#stringToBool(response.data.twoAuthNeeded)
        };
        loginResult = { status: response.status, ...mfaStatus };

        if (response.data.device_trust_token) {
          this.setDeviceTrustToken(loginData.userName, response.data.device_trust_token);
        }

        this.setAuthDataCache({
          token: response.data.access_token,
          isSuper: false,
          userName: loginData.userName,
          refreshToken: response.data.refresh_token,
          useRefreshTokens: true,
          mfaEnforceDate: response.data.twoAuthEnforceDate,
          ...mfaStatus
        });
        this.authentication.isAuth = true;
        this.authentication.userName = loginData.userName;
        this.authentication.useRefreshTokens = true;
        this.isISuper().then(isSuper => {
          if (!isSuper) {
            return;
          }

          this.authentication.isSuper = true;
          this.setAuthDataCache({
            token: response.data.access_token,
            isSuper: true,
            userName: loginData.userName,
            refreshToken: response.data.refresh_token,
            useRefreshTokens: true,
            mfaEnforceDate: response.data.twoAuthEnforceDate,
            ...mfaStatus
          });
        });
        // Resets the auth module only: the account state must survive a login that finishes after the account loaded.
        this.VuexStore.commit(`auth/${RESET_AUTH_STATE}`);

        if (window.$crisp) {
          window.$crisp.push(['set', 'user:email', this.authentication.userName]);
        }

        // This is to fetch the userUISettings and save it in the cache
        return this.userSettingsService.getUserSettings();
      })
      .then( () => {
        return loginResult;
      });
  }

  logOut() {
    const accountsService = this.$injector.get('accountsService');
    this.cacheService.remove(this._authDataCacheKey);
    this.cacheService.remove('apitoken.userAuthenticated');
    this.userSettingsService.removeUserUISettingsFromCache();

    this.authentication.isAuth = false;
    this.authentication.isSuper = false;
    this.authentication.userName = '';
    this.authentication.useRefreshTokens = false;

    accountsService.resetAccountData();
    this.promiseCacheService.clearAll();
    this.VuexStore.commit(this.VuexMutationTypes.RESET);

    this.$state.go('login');
  }

  fillAuthData() {
    const authData = this.cacheService.get(this._authDataCacheKey);
    if (authData) {
      this.authentication.isAuth = true;
      this.authentication.isSuper = authData.isSuper;
      this.authentication.userName = authData.userName;
      this.authentication.useRefreshTokens = authData.useRefreshTokens;
    }
  }

  refreshToken() {
    const deferred = this.$q.defer();

    const authData = this.cacheService.get(this._authDataCacheKey);

    this.$log.getInstance('authService').info('Refreshingtoken ', authData);

    if (authData && authData.useRefreshTokens) {
      const data = 'grant_type=refresh_token&refresh_token=' + authData.refreshToken + '&client_id=' + this.ngAuthSettings.clientId;
      this.cacheService.remove(this._authDataCacheKey);
      this.$http.post(this.serviceBase + 'token', data, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }).then((response) => {

        this.setAuthDataCache({
          token: response.data.access_token,
          userName: response.data.userName,
          isSuper: false,
          refreshToken: response.data.refresh_token,
          useRefreshTokens: true
        });

        this.isISuper().then(isSuper => {
          if (!isSuper) {
            return;
          }

          this.authentication.isSuper = true;
          this.setAuthDataCache({
            token: response.data.access_token,
            userName: response.data.userName,
            isSuper: true,
            refreshToken: response.data.refresh_token,
            useRefreshTokens: true
          });
        });

        deferred.resolve(response);
      }, (err) => {
        this.logOut();
        deferred.reject(err);
      });

    } else {
      this.logOut();
      deferred.reject();
    }

    return deferred.promise;
  }

  setPassword(data) {
    const url = this.serviceBase + 'account/confirmEmail';
    return this.$http.post(url, data);
  }

  resetPassword(data) {
    const url = this.serviceBase + 'account/resetPassword';
    return this.$http.post(url, data);
  }

  requestPasswordReset(data) {
    const url = this.serviceBase + 'account/forgotPassword';
    return this.$http.post(url, data);
  }

  isISuper() {
    const url = this.serviceBase + 'uiUserSettings/isisuper';
    return this.$http.post(url, '')
      .then(() => true)
      .catch(() => false);
  }

  setAuthDataCache(data) {
    this.cacheService.set(this._authDataCacheKey, data, {
      persistence: true
    });
  }

  // Kept out of the auth data cache on purpose: that entry is removed on logout and device trust
  // has to outlive it. Key and normalization are a cross-portal contract shared with portal-v3.
  #deviceTrustCacheKey(userName) {
    return 'deviceTrust.' + String(userName).trim().toLowerCase();
  }

  getDeviceTrustToken(userName) {
    return userName ? this.cacheService.get(this.#deviceTrustCacheKey(userName)) : null;
  }

  setDeviceTrustToken(userName, token) {
    if (!userName || !token) {
      return;
    }

    this.cacheService.set(this.#deviceTrustCacheKey(userName), token, {
      persistence: true
    });
  }

  clearDeviceTrustToken(userName) {
    if (!userName) {
      return;
    }

    this.cacheService.remove(this.#deviceTrustCacheKey(userName));
  }
}
